import React from 'react';
import { useStoreState } from 'easy-peasy';
import styled from 'styled-components';

import { numberFormat, getQualificationLabel } from '~/utils';
import { getCircleColor } from './chart-utils';

const tooltipWidth = 200;

function clamp(min, val, max) {
  return Math.max(Math.min(max, val), min);
}

const TooltipWrapper = styled.div.attrs((props) => ({
  style: { top: props.y, left: props.x },
}))`
  position: absolute;
  z-index: 100;
  pointer-events: none;
  user-select: none;
  width: ${tooltipWidth}px;
  font-size: 12px;
  overflow: visible;
  transform: translateY(${(p) => p.transformY});
  padding: 10px 0;
`;

const TooltipContent = styled.div`
  box-shadow: 0px 0px 5px 0px rgba(50, 50, 50, 0.3);
  border-radius: 2px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.95);
  padding: 8px 8px 0 8px;
`;

const TooltipItem = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 6px;
  padding-bottom: 6px;
  border-bottom: 1px solid #ddd;

  &:last-child {
    border-bottom: none;
  }
`;

const TooltipLabel = styled.div`
  font-weight: ${(p) => (p.light ? 'normal' : 'bold')};
  color: ${(p) => p.color || '#000'};
`;

const TooltipKey = styled.div``;

const TooltipValue = styled.div`
  font-weight: bold;
  min-width: 50px;
  text-align: right;
`;

const filterMessages = {
  all: null,
  female: '(nur Frauen)',
  male: '(nur Männer)',
  under40: '(nur unter 40 Jahre)',
  over40: '(nur über 40 Jahre)',
  south: '(nur Süddeutschland)',
  west: '(nur Nord- und Westdeutschland)',
  east: '(nur Ostdeutschland)',
};

const Tooltip = ({ xScale, yScale, margin, width, height }) => {
  const data = useStoreState((state) => state.tooltip.data);
  const filterDemographics = useStoreState(
    (state) => state.app.filterDemographics
  );

  const filterMessage = filterMessages[filterDemographics];

  if (!data) {
    return null;
  }

  const xPos = xScale(data.x) + margin.left - tooltipWidth / 2;
  const x = clamp(0, xPos, width - tooltipWidth);
  const y = yScale(data.y) + margin.top;
  const transformY = y < height / 2 ? '0' : '-100%';

  const yVal = numberFormat(data.y, 0, '€');
  const yPre = data.y >= 6150 ? '≥ ' : '';

  return (
    <TooltipWrapper x={x} y={y} transformY={transformY}>
      <TooltipContent>
        <TooltipLabel>{data.label}</TooltipLabel>
        <TooltipLabel light color={getCircleColor(data)}>
          {getQualificationLabel(data.qualification)}
        </TooltipLabel>
        <TooltipItem>
          <TooltipKey>
            mittlerer Bruttolohn{filterMessage && ` ${filterMessage}`}
          </TooltipKey>
          <TooltipValue>
            {yPre}
            {yVal}
          </TooltipValue>
        </TooltipItem>
        <TooltipItem>
          <TooltipKey>Arbeitslose je 100 offene Stellen</TooltipKey>
          <TooltipValue>{numberFormat(data.unemployment_abs, 0)}</TooltipValue>
        </TooltipItem>
        <TooltipItem>
          <TooltipKey>
            Beschäftigte{filterMessage && ` ${filterMessage}`}
          </TooltipKey>
          <TooltipValue>{numberFormat(data.r, 0)}</TooltipValue>
        </TooltipItem>
      </TooltipContent>
    </TooltipWrapper>
  );
};

export default Tooltip;
